import React, { useEffect, useState } from 'react';
import { X, Send } from 'lucide-react';
import { submitContact } from '../../lib/api';
import { withTimeout } from '../../lib/utils';
import { Input } from '../ui/input';

const SUBMIT_TIMEOUT_MS = 4000;

export default function ProgramLeadPopup({ program, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const contactPromise = submitContact({
      ...form,
      course: program.title,
      message: `Landing page popup enquiry for ${program.title}`,
      source: 'program_landing_page_popup',
      page: `program_landing_page_popup:${program.slug}`,
    });

    const result = await withTimeout(contactPromise, SUBMIT_TIMEOUT_MS);
    setLoading(false);
    if (!result.timedOut && result.error) {
      setError('Something went wrong. Please try again.');
      return;
    }
    onSuccess?.(form);
  };

  return (
    <div
      data-testid="program-lead-popup"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F1F3D]/70 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E1E7EF]">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-wider mb-0.5"
              style={{ color: program.accent }}
            >
              {program.title}
            </p>
            <h2 className="font-heading font-bold text-lg text-[#1F1F1F]">
              Get Course Details
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="program-lead-popup-close"
            className="w-8 h-8 rounded-full bg-[#F0F0F0] hover:bg-[#E0E0E0] flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-[#585858]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-[#585858]">
            Share your details and our counsellor will reach out to you about the {program.title} program.
          </p>

          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Full Name *</label>
            <Input
              data-testid="popup-lead-name-input"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Your full name"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Email *</label>
            <Input
              data-testid="popup-lead-email-input"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
              placeholder="your@email.com"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1F1F1F] mb-1 block">Phone Number *</label>
            <Input
              data-testid="popup-lead-phone-input"
              type="tel"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              required
              placeholder="+91 XXXXX XXXXX"
              className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="popup-lead-submit-btn"
            className="w-full text-sm font-semibold py-3 rounded-md text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
            style={{ backgroundColor: program.accent }}
          >
            <Send className="w-4 h-4" />
            {loading ? 'Submitting...' : 'Request Callback'}
          </button>
          <p className="text-[11px] text-center text-[#9AA5B5]">
            By submitting, you agree to be contacted about this course.
          </p>
        </form>
      </div>
    </div>
  );
}
